import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import mealApi from "../../../api/meal/mealApi";
import type { MealCheckActiveOraData } from "../../../api/meal/types/mealTodayType";
import {
  isRequestCancelled,
  type RejectPayload,
  toRejectPayload,
} from "../../../helpers/ApiError";
import type { RootState } from "../../store";

interface MealCheckActiveOraState {
  data: MealCheckActiveOraData | null;
  isLoading: boolean;
  error: string | null;
  requestId: string | null;
}

const initialState: MealCheckActiveOraState = {
  data: null,
  isLoading: false,
  error: null,
  requestId: null,
};

export const fetchMealCheckActiveOra = createAsyncThunk<
  MealCheckActiveOraData,
  void,
  { rejectValue: RejectPayload }
>(
  "mealCheckActiveOra/fetchMealCheckActiveOra",
  async (_, { rejectWithValue, signal }) => {
    try {
      const res = await mealApi.getCheckActiveOra(signal);
      return res.data;
    } catch (err) {
      if (isRequestCancelled(err, signal)) {
        throw err;
      }

      return rejectWithValue(
        toRejectPayload(err, "Gagal memuat data active ORA"),
      );
    }
  },
);

const mealCheckActiveOraSlice = createSlice({
  name: "mealCheckActiveOra",
  initialState,
  reducers: {
    resetMealCheckActiveOra: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMealCheckActiveOra.pending, (state, action) => {
        state.data = null;
        state.isLoading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchMealCheckActiveOra.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
        state.requestId = null;
      })
      .addCase(fetchMealCheckActiveOra.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;

        state.isLoading = false;
        state.requestId = null;

        if (action.meta.aborted) {
          state.error = null;
          return;
        }

        state.data = null;
        state.error = action.payload?.message ?? "Gagal memuat data active ORA";
      });
  },
});

export const { resetMealCheckActiveOra } = mealCheckActiveOraSlice.actions;

export const selectMealCheckActiveOra = (state: RootState) =>
  state.mealCheckActiveOraReducer.data;
export const selectMealCheckActiveOraLoading = (state: RootState) =>
  state.mealCheckActiveOraReducer.isLoading;
export const selectMealCheckActiveOraError = (state: RootState) =>
  state.mealCheckActiveOraReducer.error;

export default mealCheckActiveOraSlice.reducer;
